import { tx as translateInterfaceText } from '@/utils/i18n';
import { useLanguage } from '@/hooks/useLanguage';
import React, { useEffect } from 'react';
import { VideoFeatures } from '@/store/videoFeatures';
import { ColorEditorPopover } from '../autoplay/ColorEditorPopover';
import { stringToRgba } from '@/components/ColorPickerRGBA';
import { Palette } from 'lucide-react';

interface PlayerThemeCardProps {
  features: VideoFeatures;
  setFeature: <K extends keyof VideoFeatures>(key: K, value: VideoFeatures[K]) => void;
}

export const PlayerThemeCard: React.FC<PlayerThemeCardProps> = ({ features, setFeature }) => {
  useLanguage();
  const playerColors = features.playerColors || {
    backgroundColor: '#8791A3',
    borderRadius: 4,
    opacity: 0.45
  };

  // Estados para controle de drag dos sliders premium - COMENTADO TEMPORARIAMENTE
  // const [draggingBorderRadius, setDraggingBorderRadius] = useState(false);
  // const [draggingOpacity, setDraggingOpacity] = useState(false);
  // const borderRadiusRef = useRef<HTMLDivElement>(null);
  // const opacityRef = useRef<HTMLDivElement>(null);

  const updatePlayerColors = (key: keyof typeof playerColors, value: any) => {
    // IMPORTANTE: Usar callback para garantir que pegamos o valor mais recente
    setFeature('playerColors', {
      ...features.playerColors, // ← Sempre pegar do features atual
      [key]: value
    });
  };

  const hexToRgba = (hex: string, opacity: number = 1): string => {
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);
    return `rgba(${r}, ${g}, ${b}, ${opacity})`;
  };

  // Handler genérico para slider premium - COMENTADO TEMPORARIAMENTE
  /* const handleSliderDrag = useCallback((
    e: React.MouseEvent<HTMLDivElement>,
    ref: React.RefObject<HTMLDivElement>,
    min: number,
    max: number,
    step: number,
    onUpdate: (value: number) => void,
    setDragging: (v: boolean) => void
  ) => {
    e.preventDefault();
    setDragging(true);

    const sliderEl = ref.current;
    if (!sliderEl) return;

    const rect = sliderEl.getBoundingClientRect();
    const calculateValue = (clientX: number) => {
      const percentage = (clientX - rect.left) / rect.width;
      const rawValue = min + percentage * (max - min);
      const snappedValue = Math.round(rawValue / step) * step;
      return Math.max(min, Math.min(max, snappedValue));
    };

    onUpdate(calculateValue(e.clientX));

    const handleMouseMove = (moveEvent: MouseEvent) => {
      onUpdate(calculateValue(moveEvent.clientX));
    };

    const handleMouseUp = () => {
      setDragging(false);
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  }, []); */

  useEffect(() => {
    if (!features.playerThemeEnabled) {
      setFeature('playerThemeEnabled', true);
    }
  }, [features.playerThemeEnabled, setFeature]);

  return (
    <div className="bg-white rounded-[12px] border border-[#E4E7EC] p-3.5">
      <div className="flex items-center gap-3">
        {/* Icon Box */}
        <div className="flex-shrink-0 w-9 h-9 rounded-lg flex items-center justify-center bg-[#1D4FFF]/10 text-[#1D4FFF]">
          <Palette className="w-4 h-4" strokeWidth={2} />
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          <h4 className="text-[13px] font-semibold text-[#1A1A1A] leading-tight">
            {translateInterfaceText("Personalizar Tema")}</h4>
          <p className="text-[11px] text-[#6B7280] leading-snug mt-0.5">
            {translateInterfaceText("Personalize as cores e aparência do player.")}</p>
        </div>
      </div>

      <div className="mt-3 pt-3 border-t border-[#E2E8F0]/60 space-y-3">
              {/* Grid com 3 cores lado a lado */}
              <div className="grid grid-cols-3 gap-3">
                {/* Cor dos Controles */}
                <div className="flex flex-col items-center space-y-2 mt-1">
                  <span className="text-[12px] font-medium text-[#374151]">{translateInterfaceText("Cor dos Controles")}</span>
                  <ColorEditorPopover
                    colorName={translateInterfaceText("Cor dos controles")}
                    currentColor={hexToRgba(
                      playerColors.backgroundColor || '#8791A3',
                      playerColors.opacity || 0.45
                    )}
                    onSave={(newColor) => {
                      const rgba = stringToRgba(newColor);
                      const hex = `#${rgba.r.toString(16).padStart(2, '0')}${rgba.g.toString(16).padStart(2, '0')}${rgba.b.toString(16).padStart(2, '0')}`;
                      // IMPORTANTE: Atualizar ambos de uma vez para evitar race condition
                      setFeature('playerColors', {
                        ...features.playerColors,
                        backgroundColor: hex,
                        opacity: rgba.a
                      });
                      // Salvar também o controlColor para o backend
                      setFeature('controlColor', newColor);
                    }}
                  >
                    <button
                      type="button"
                      className="w-10 h-10 rounded-full border-2 border-white shadow-md hover:scale-110 transition-transform cursor-pointer"
                      style={{
                        backgroundColor: hexToRgba(
                          playerColors.backgroundColor || '#8791A3',
                          playerColors.opacity || 0.45
                        )
                      }}
                    />
                  </ColorEditorPopover>
                </div>

                {/* Cor do Play Grande */}
                <div className="flex flex-col items-center space-y-2 mt-1">
                  <span className="text-[12px] font-medium text-[#374151]">{translateInterfaceText("Cor do Play Grande")}</span>
                  <ColorEditorPopover
                    colorName={translateInterfaceText("Cor do play grande")}
                    currentColor={features.controlColorIcon || 'rgba(255, 255, 255, 1)'}
                    onSave={(newColor) => {
                      setFeature('controlColorIcon', newColor);
                    }}
                  >
                    <button
                      type="button"
                      className="w-10 h-10 rounded-full border-2 border-white shadow-md hover:scale-110 transition-transform cursor-pointer"
                      style={{
                        backgroundColor: features.controlColorIcon || 'rgba(255, 255, 255, 1)'
                      }}
                    />
                  </ColorEditorPopover>
                </div>

                {/* Cor de Fundo da Barra */}
                <div className="flex flex-col items-center space-y-2 mt-1">
                  <span className="text-[12px] font-medium text-[#374151]">{translateInterfaceText("Cor do Rodapé")}</span>
                  <ColorEditorPopover
                    colorName={translateInterfaceText("Cor do rodapé")}
                    currentColor={features.controlBarColor || 'rgba(135, 145, 163, 0.45)'}
                    onSave={(newColor) => {
                      setFeature('controlBarColor', newColor);
                    }}
                  >
                    <button
                      type="button"
                      className="w-10 h-10 rounded-full border-2 border-white shadow-md hover:scale-110 transition-transform cursor-pointer"
                      style={{
                        backgroundColor: features.controlBarColor || 'rgba(135, 145, 163, 0.45)'
                      }}
                    />
                  </ColorEditorPopover>
                </div>
              </div>

              {/* Raio dos Cantos - Slider Premium - COMENTADO TEMPORARIAMENTE */}
              {/* <div className="space-y-1.5">
                <div className="flex items-center justify-between">
                  <span className="text-[12px] font-medium text-[#1A1A1A]">Raio dos Cantos</span>
                  <span className="text-[11px] text-[#1D4FFF] font-medium">{playerColors.borderRadius || 4}px</span>
                </div>

                <div className="relative h-8 flex items-center px-1">
                  <div
                    ref={borderRadiusRef}
                    className={cn(
                      "relative h-1 w-full bg-[#E6EBF2] rounded-full overflow-visible",
                      draggingBorderRadius ? "cursor-grabbing" : "cursor-grab"
                    )}
                    onMouseDown={(e) => handleSliderDrag(
                      e,
                      borderRadiusRef,
                      0, 24, 4,
                      (v) => updatePlayerColors('borderRadius', v),
                      setDraggingBorderRadius
                    )}
                  >
                    <div
                      className={cn(
                        "absolute inset-y-0 left-0 rounded-full bg-[#1D4FFF] transition-all",
                        draggingBorderRadius ? "duration-0" : "duration-150"
                      )}
                      style={{ width: `${((playerColors.borderRadius || 4) / 24) * 100}%` }}
                    />
                    <div
                      className={cn(
                        "absolute top-1/2 -translate-y-1/2 w-3 h-3 bg-white rounded-full border-2 border-[#1D4FFF] shadow-[0_2px_6px_rgba(0,0,0,0.15)] transition-all",
                        draggingBorderRadius
                          ? "scale-110 duration-0"
                          : "duration-150 hover:scale-110 hover:shadow-[0_2px_8px_rgba(0,0,0,0.2)]"
                      )}
                      style={{ left: `calc(${((playerColors.borderRadius || 4) / 24) * 100}% - 6px)` }}
                    />
                  </div>
                </div>
              </div> */}

              {/* Opacidade - Slider Premium - COMENTADO TEMPORARIAMENTE */}
              {/* <div className="space-y-1.5">
                <div className="flex items-center justify-between">
                  <span className="text-[12px] font-medium text-[#1A1A1A]">Opacidade</span>
                  <span className="text-[11px] text-[#1D4FFF] font-medium">{Math.round((playerColors.opacity || 0.45) * 100)}%</span>
                </div>

                <div className="relative h-8 flex items-center px-1">
                  <div
                    ref={opacityRef}
                    className={cn(
                      "relative h-1 w-full bg-[#E6EBF2] rounded-full overflow-visible",
                      draggingOpacity ? "cursor-grabbing" : "cursor-grab"
                    )}
                    onMouseDown={(e) => handleSliderDrag(
                      e,
                      opacityRef,
                      10, 100, 5,
                      (v) => updatePlayerColors('opacity', v / 100),
                      setDraggingOpacity
                    )}
                  >
                    <div
                      className={cn(
                        "absolute inset-y-0 left-0 rounded-full bg-[#1D4FFF] transition-all",
                        draggingOpacity ? "duration-0" : "duration-150"
                      )}
                      style={{ width: `${(((playerColors.opacity || 0.45) * 100) - 10) / 90 * 100}%` }}
                    />
                    <div
                      className={cn(
                        "absolute top-1/2 -translate-y-1/2 w-3 h-3 bg-white rounded-full border-2 border-[#1D4FFF] shadow-[0_2px_6px_rgba(0,0,0,0.15)] transition-all",
                        draggingOpacity
                          ? "scale-110 duration-0"
                          : "duration-150 hover:scale-110 hover:shadow-[0_2px_8px_rgba(0,0,0,0.2)]"
                      )}
                      style={{ left: `calc(${(((playerColors.opacity || 0.45) * 100) - 10) / 90 * 100}% - 6px)` }}
                    />
                  </div>
                </div>
              </div> */}
      </div>
    </div>
  );
};
